/**
 * PeopleInsidePanel — "Pessoas no Estabelecimento"
 *
 * Exibido apenas quando a organização tem câmeras de saída configuradas
 * (systemStats.has_exit_cameras === true). Mostra:
 *   - Pessoas no Estabelecimento (Entradas − Saídas) — destaque
 *   - Entradas
 *   - Saídas
 *
 * Use case: eventos com 1 entrada + 1 saída — o cliente quer saber
 * quantas pessoas estão no evento naquele momento.
 *
 * Quando filtrado (período custom/câmera), usa os dados de
 * FilteredStats (summary.total_people / exit_people / inside_people).
 * Quando não filtrado (padrão/hoje), usa os dados em tempo real de
 * SystemStatsComplete (today_people / today_exit_people / today_inside_people).
 */

import React from 'react';
import { SystemStatsComplete } from '../../domain/entities/Analytics';
import { FilteredStats } from '../../data/services/ReportsService';

interface PeopleInsidePanelProps {
  systemStats: SystemStatsComplete | null;
  filteredStats?: FilteredStats | null;
  isFiltered?: boolean;
}

export const PeopleInsidePanel: React.FC<PeopleInsidePanelProps> = ({
  systemStats,
  filteredStats,
  isFiltered = false,
}) => {
  // ⚠️ Só renderiza quando há câmeras de saída configuradas.
  if (!systemStats?.has_exit_cameras) return null;

  // 🛡️ Robustez: se isFiltered=true mas filteredStats ainda não carregou
  // (transição), cai para os dados de hoje (mesmo padrão do displayStats).
  const usingFiltered = isFiltered && !!filteredStats;

  const entradas = usingFiltered
    ? (filteredStats?.summary.total_people ?? 0)
    : (systemStats.today_people ?? 0);
  const saidas = usingFiltered
    ? (filteredStats?.summary.exit_people ?? 0)
    : (systemStats.today_exit_people ?? 0);
  const inside = usingFiltered
    ? (filteredStats?.summary.inside_people ?? entradas - saidas)
    : (systemStats.today_inside_people ?? entradas - saidas);

  // 🔒 Clamp em 0: mais pessoas saíram do que foram contadas entrando pode
  //    acontecer quando a contagem começou após o início do evento.
  const insideDisplay = Math.max(0, inside);
  const periodLabel = isFiltered ? 'Período' : 'Hoje';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
      {/* Pessoas no Estabelecimento (destaque) */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-3 sm:p-4 md:p-6 rounded-lg shadow-md flex flex-col h-full">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-[10px] sm:text-xs md:text-sm text-blue-100 font-semibold">
            🏢 Pessoas no Estabelecimento ({periodLabel})
          </p>
        </div>
        <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white my-auto">
          {insideDisplay.toLocaleString('pt-BR')}
        </p>
        <p className="text-[9px] sm:text-xs text-blue-200 mt-1 sm:mt-2">
          {entradas.toLocaleString('pt-BR')} entraram — {saidas.toLocaleString('pt-BR')} saíram
        </p>
      </div>

      {/* Entradas */}
      <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
        <div className="flex flex-col gap-1 mb-2">
          <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">
            Entradas ({periodLabel})
          </p>
          <span className="text-[9px] sm:text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 sm:px-2 py-0.5 rounded-full w-fit">
            🚪 Entrada
          </span>
        </div>
        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400 my-auto">
          {entradas.toLocaleString('pt-BR')}
        </p>
      </div>

      {/* Saídas */}
      <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
        <div className="flex flex-col gap-1 mb-2">
          <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">
            Saídas ({periodLabel})
          </p>
          <span className="text-[9px] sm:text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-1.5 sm:px-2 py-0.5 rounded-full w-fit">
            🚪 Saída
          </span>
        </div>
        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-red-600 dark:text-red-400 my-auto">
          {saidas.toLocaleString('pt-BR')}
        </p>
      </div>
    </div>
  );
};
